import React, { useContext } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./app/App";
import * as serviceWorker from "./serviceWorker";
import AepThemeProvider from "./styles/AepThemeProvider";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import { keycloak, keycloakInitOptions, keycloakOnEvent } from "./keycloak";
import ContextsProvider from "./app/contexts/ContextsProvider";
import { CustomerContext } from "./app/contexts/CustomerContext";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import de from "date-fns/locale/de";

import { ApplicationInsights } from '@microsoft/applicationinsights-web';

// Get Application Insights connection string from runtime or build-time env
const getAppInsightsConnectionString = () => {
  if (window._env_ && window._env_.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING) {
    return window._env_.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING;
  }
  if (process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING) {
    return process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING;
  }
  return undefined;
};

const appInsightsConnectionString = getAppInsightsConnectionString();

// Validate connection string format (should start with "InstrumentationKey=" or be undefined)
const isValidConnectionString = appInsightsConnectionString && 
  (appInsightsConnectionString.includes('InstrumentationKey=') || 
   appInsightsConnectionString.includes('IngestionEndpoint='));

if (!appInsightsConnectionString || !isValidConnectionString) {
  // eslint-disable-next-line no-console
  console.warn('Azure Application Insights connection string is missing or invalid - telemetry disabled');
}

const appInsights = new ApplicationInsights({
    config: {
        connectionString: isValidConnectionString ? appInsightsConnectionString : undefined,
        enableDebug: false,                 // enables verbose debug output in console
        loggingLevelConsole: 1,
        enableAutoRouteTracking: true, // Track SPA route changes
        disableTelemetry: !isValidConnectionString, // Disable if no valid connection string
    }
});

// Set custom application name for Azure Application Insights
const APP_INSIGHTS_CLOUD_ROLE = "AEP Frontend Portal"; // Change as desired

appInsights.addTelemetryInitializer((envelope) => {
  if (envelope.tags) {
    envelope.tags["ai.cloud.role"] = APP_INSIGHTS_CLOUD_ROLE;
  }

  // Add custom dimensions for all telemetry
  if (envelope.baseData && keycloak.authenticated && keycloak.tokenParsed) {
    const customDimensions = envelope.baseData.properties || {};
    customDimensions.defaultPharmacy = keycloak.tokenParsed.defaultPharmacy;
    customDimensions.pharmacyAdmin = keycloak.tokenParsed.realm_access?.roles?.includes("apoportal-pharmacy-admin");
    customDimensions.pharmacyUser  = keycloak.tokenParsed.realm_access?.roles?.includes("apoportal-user");

    envelope.baseData.properties = customDimensions;
  }
});

if (isValidConnectionString && appInsightsConnectionString) {
  appInsights.loadAppInsights();
  appInsights.trackPageView(); // initial pageview
}

export default appInsights;


//React Render
const container = document.getElementById("root");
if (!container) throw new Error('Failed to find the root element');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
      <ReactKeycloakProvider
        authClient={keycloak}
        initOptions={keycloakInitOptions}
        onEvent={keycloakOnEvent}
      >
        <AepThemeProvider>
          <ContextsProvider>
            <BrowserRouter basename="/">
              <App />
            </BrowserRouter>
          </ContextsProvider>
        </AepThemeProvider>
      </ReactKeycloakProvider>
    </LocalizationProvider>
  </React.StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();

